import {put, call, all} from 'redux-saga/effects';
import Firm from "../../entities/firm";
import * as Actions from "../actions/firmActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type FirmAction = {
    firmId: string;
    firm: Firm;
    firms: Firm[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneFirm(action: FirmAction) {
	const session = tabSession.captureGeneration();
	try {
		const firm: Firm = yield call(api.findOne, 'firm', action.firmId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.FIRM_FETCH_ONE_SUCCESS, firmId: action.firmId, firm, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FIRM_FETCH_ONE_FAILURE, firmId: action.firmId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {FirmAction} action
 */
export function* saveFirm(action: FirmAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ FIRM_SAVE saga', action.firm?.id);

		if (action.firm === undefined || action.firm === null) {
			console.error('saveFirm received a null');
			throw new Error('saveFirm received a null');
		}

		if (!(action.firm instanceof Firm)) {
			action.firm = new Firm(action.firm);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.firm.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.firm);
			const addResult = yield call(idb.add, 'firm', action.firm);
			if (addResult.isErr()) {
				console.error(`⮂ saveFirm: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertFirmSave(action.firm, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.FIRM_SAVE_FAILURE, firm: action.firm, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.FIRM_SAVE, firm: action.firm, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertFirmSave(action.firm, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.FIRM_SAVE_FAILURE, firm: action.firm, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {FirmAction} action
 * @param {Firm[]} action.firms
 */
export function* saveManyFirms(action: FirmAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ FIRM_SAVE_MANY saga', action.firms.map(firm => firm.id).join(','));

        if (!Array.isArray(action.firms)) {
            console.error('saveManyFirms expected an array, received', action.firms);
            throw new Error('saveManyFirms expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveFirm), under these queue keys
		const dispatchedAddedOns: number[] = action.firms.map(firm => firm?.addedOn);
		const revertAll = () => action.firms.map((firm, i) =>
			put(Actions.revertFirmSave(firm, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Firm[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.firms.length; i++) {
				const firm = action.firms[i];
				firm.persisted = false;
				ensureOperationId(firm);
				toSave.push(firm);
			}

			const addResult = yield call(idb.add, 'firm', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyFirms: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.FIRM_SAVE_MANY_FAILURE, firms: action.firms, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.FIRM_SAVE_MANY, firms: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.FIRM_SAVE_MANY_FAILURE, firms: action.firms, status: 500, err});

			console.error(`Error saving ${action.firms.length} firms to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {FirmAction} action
 */
export function* removeFirm(action: FirmAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.firm === undefined || action.firm === null || !(action.firm instanceof Firm)) {
			console.error('removeFirm expected type Firm, received', action.firm);
			reportQueued(false);
			throw new Error('removeFirm expected type Firm');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.firm.status = 'D';
		action.firm.addedOn = newHRTimestamp();
		action.firm.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.firm);
		const addResult = yield call(idb.add, 'firm', action.firm);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeFirm: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.FIRM_REMOVE_FAILURE, firm: action.firm, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FIRM_REMOVE, firm: action.firm});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FIRM_REMOVE_FAILURE, firm: action.firm, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Firm.
	*
	* @param {FirmAction} action
	*/
export function* restoreFirm(action: FirmAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreFirm expected getCurrentUserId() to return a value');
        return;
    }

	if (action.firm === undefined || action.firm === null) {
        console.error('restoreFirm expected a Firm, received null');
        throw new Error('restoreFirm expected a Firm, received null');
    }
    else if (!(action.firm instanceof Firm)) action.firm = new Firm(action.firm); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.firm.status = 'U';
		action.firm.addedOn = newHRTimestamp();
		action.firm.addedBy = userId;
		action.firm.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.firm);
		const addResult = yield call(idb.add, 'firm', action.firm);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreFirm: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.FIRM_RESTORE_FAILURE, firm: action.firm, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FIRM_RESTORE, firm: action.firm});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FIRM_RESTORE_FAILURE, firm: action.firm, status: 500, err});

		console.error('Error restoring from database', err);
	}
}